import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';

import {
  Affluence,
  Coverage,
  TeamSize,
  ServiceTime,
  Periodicity,
} from 'src/app/models/onboarding.model';
import type { Positioning } from 'src/app/models/cluster.model';

import {
  AFFLUENCE_LABELS,
  SERVICE_LABELS,
  PERIODICITY_LABELS,
  TEAM_SIZE_LABELS,
  COVERAGE_OPTIONS,
} from 'src/app/models/onboarding.labels';
import { PositioningSelectorComponent } from 'src/app/shared/components/positioning-selector/positioning-selector.component';
import { LogService } from '../../../services/log.service';

interface ContextFixedForm {
  affluence: Affluence;
  coverage: Coverage;
  teamSize: TeamSize;
  services: ServiceTime[];
  periodicity: Periodicity;
}

@Component({
  selector: 'app-step-context-fixed',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressBarComponent,
    PositioningSelectorComponent,
  ],
  templateUrl: './step-context-fixed.component.html',
})
export class StepContextFixedComponent implements OnInit {
  private log = inject(LogService);
  selectedPositioning: Positioning = 'standard';
  currentStep = 4;
  totalSteps = 6;

  form!: FormGroup;

  readonly DEFAULT_CONTEXT: ContextFixedForm = {
    affluence: Affluence.MEDIUM,
    coverage: Coverage.UNCOVERED,
    teamSize: TeamSize.SOLO,
    services: ['Déjeuner'],
    periodicity: Periodicity.DAILY,
  };

  /* Labels exposés au template */
  affluenceLabels = AFFLUENCE_LABELS;
  serviceLabels = SERVICE_LABELS;
  coverageOptions = COVERAGE_OPTIONS;
  periodicityLabels = PERIODICITY_LABELS;
  teamSizeLabels = TEAM_SIZE_LABELS;

  // Ordre spécifique : Solo en premier
  teamSizeOptions = [
    { key: 'solo', ...TEAM_SIZE_LABELS['solo'] },
    { key: '2persons', ...TEAM_SIZE_LABELS['2persons'] },
    { key: '3plus', ...TEAM_SIZE_LABELS['3plus'] },
    { key: 'multi_stand', ...TEAM_SIZE_LABELS['multi_stand'] },
  ];

  // Ordre spécifique : Faible → Moyen → Élevé
  affluenceOptions = [
    { key: 'low', ...AFFLUENCE_LABELS['low'] },
    { key: 'medium', ...AFFLUENCE_LABELS['medium'] },
    { key: 'high', ...AFFLUENCE_LABELS['high'] },
  ];

  // Ordre spécifique : Petit-déj → Déjeuner → Dîner
  serviceOptions = [
    { key: 'Petit-déjeuner', label: SERVICE_LABELS['Petit-déjeuner'] },
    { key: 'Déjeuner', label: SERVICE_LABELS['Déjeuner'] },
    { key: 'Dîner', label: SERVICE_LABELS['Dîner'] },
  ];

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private fb: FormBuilder,
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private router: Router,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.onboardingStateService.setCurrentStep(4);
    const data = this.onboardingStateService.getFormData();

    if (!data) return;

    this.form.patchValue({
      affluence: data.affluence ?? this.DEFAULT_CONTEXT.affluence,
      coverage: data.coverage ?? this.DEFAULT_CONTEXT.coverage,
      teamSize: data.teamSize ?? this.DEFAULT_CONTEXT.teamSize,
      services: data.services ?? this.DEFAULT_CONTEXT.services,
      periodicity: data.periodicity ?? this.DEFAULT_CONTEXT.periodicity,
    });

    // Restaurer le positionnement
    if (data.positioning) {
      this.selectedPositioning = data.positioning;
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      affluence: [this.DEFAULT_CONTEXT.affluence, Validators.required],
      coverage: [this.DEFAULT_CONTEXT.coverage, Validators.required],
      teamSize: [this.DEFAULT_CONTEXT.teamSize, Validators.required],
      services: [this.DEFAULT_CONTEXT.services, Validators.required],
      periodicity: [this.DEFAULT_CONTEXT.periodicity, Validators.required],
    });
  }

  selectAffluence(value: Affluence): void {
    this.form.patchValue({ affluence: value });
  }

  isServiceSelected(service: ServiceTime): boolean {
    return this.form.value.services.includes(service);
  }

  toggleService(service: ServiceTime): void {
    const services: ServiceTime[] = this.form.value.services;
    this.form.patchValue({
      services: services.includes(service)
        ? services.filter((s) => s !== service)
        : [...services, service],
    });
  }

  selectPeriodicity(value: Periodicity): void {
    this.form.patchValue({ periodicity: value });
  }

  selectCoverage(value: Coverage): void {
    this.form.patchValue({ coverage: value });
  }

  isCoverageSelected(value: Coverage): boolean {
    return this.form.value.coverage === value;
  }

  selectTeamSize(value: TeamSize): void {
    this.form.patchValue({ teamSize: value });
  }

  isFieldSelected(fieldName: string, value: string): boolean {
    return this.form.get(fieldName)?.value === value;
  }

  /* ===== Navigation ===== */
  continue(): void {
    if (!this.form.valid) {
      this.log.warn('Formulaire invalide');
      return;
    }

    const value: ContextFixedForm = this.form.value;

    // Sauvegarder dans le state service (sessionStorage)
    this.onboardingStateService.updateFormData({
      ...value,
      positioning: this.selectedPositioning,
    });

    // Sauvegarder en DB via l'API
    this.onboardingService
      .updateOnboardingStep(4, {
        affluence: value.affluence,
        coverage: value.coverage,
        teamSize: value.teamSize,
        services: value.services,
        periodicity: value.periodicity,
        positioning: this.selectedPositioning,
      })
      .subscribe({
        next: () => {
          this.log.debug('✅ Contexte fixe sauvegardé en DB');
          // Naviguer vers l'étape 5 (produits)
          this.router.navigate(['/onboarding/step/5']);
        },
        error: (err) => {
          this.log.error('❌ Erreur sauvegarde contexte fixe:', err);
          // Continuer quand même (les données sont en sessionStorage)
          this.router.navigate(['/onboarding/step/5']);
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/onboarding/step/3']);
  }

  get validationMessage(): string {
    const v = this.form.value;
    const d = this.DEFAULT_CONTEXT;

    const isDefault =
      v.affluence === d.affluence &&
      v.coverage === d.coverage &&
      v.teamSize === d.teamSize &&
      v.periodicity === d.periodicity &&
      v.services.length === d.services.length &&
      v.services.every((s: ServiceTime, i: number) => s === d.services[i]);

    return isDefault
      ? 'Les valeurs par défaut ont été pré-remplies. Vous pouvez les ajuster.'
      : 'Configuration personnalisée. Vous pouvez continuer.';
  }
}
